<script setup lang="ts">
import { ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

interface Props {
  modelValue?: File[];
  disabled?: boolean;
  maxFiles?: number;
  // D-37 / B1: optional upload-progress map keyed by File instance. Files
  // present with a value < 100 render the uploading tile state; otherwise
  // the done tile state. Parent orchestrates the upload + progress updates.
  uploadProgress?: Map<File, number>;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  disabled: false,
  maxFiles: 10,
  uploadProgress: () => new Map<File, number>(),
});

const emit = defineEmits<{
  'update:modelValue': [files: File[]];
  cancel: [file: File];
}>();

// D-14: client-side validation. Browsers occasionally report empty MIME for
// .doc/.docx, so the extension regex acts as a fallback. The 20 MB cap matches
// the mobile MAX_FILE_SIZE constant (RESEARCH.md Pitfall 9).
const ALLOWED_DOC_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;
const ALLOWED_DOC_EXT_REGEX = /\.(pdf|docx?|DOCX?|PDF)$/i;
const DOC_MAX_BYTES = 20 * 1024 * 1024;

const uploadError = ref<string>('');
const fileInput = ref<HTMLInputElement | null>(null);

function isAllowedDoc(file: File): boolean {
  return (
    (ALLOWED_DOC_MIME as readonly string[]).includes(file.type) ||
    ALLOWED_DOC_EXT_REGEX.test(file.name)
  );
}

function isUnderSize(file: File): boolean {
  return file.size <= DOC_MAX_BYTES;
}

function processFiles(incoming: File[]): void {
  uploadError.value = '';
  const valid: File[] = [];
  for (const f of incoming) {
    if (!isAllowedDoc(f)) {
      uploadError.value = 'Only PDF, DOC, and DOCX files are allowed.';
      continue;
    }
    if (!isUnderSize(f)) {
      uploadError.value = 'Documents must be under 20MB.';
      continue;
    }
    valid.push(f);
  }
  emit(
    'update:modelValue',
    [...props.modelValue, ...valid].slice(0, props.maxFiles),
  );
}

function onFileChange(e: Event): void {
  const input = e.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  processFiles(files);
  // WR-05: clear the input so re-selecting the same file fires @change.
  // Without this, removing a file and re-picking the SAME file is a silent
  // no-op (browsers suppress duplicate-value change events). Matches
  // VideoUpload.onFileChange.
  input.value = '';
}

function onDrop(e: DragEvent): void {
  const files = Array.from(e.dataTransfer?.files ?? []);
  processFiles(files);
}

function openPicker(): void {
  fileInput.value?.click();
}

function removeFile(index: number): void {
  const next = props.modelValue.filter((_, i) => i !== index);
  emit('update:modelValue', next);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// D-37 / B1: tile state predicates.
function isUploading(file: File): boolean {
  const p = props.uploadProgress.get(file);
  return typeof p === 'number' && p < 100;
}

function progressOf(file: File): number {
  return props.uploadProgress.get(file) ?? 0;
}

function onCancel(file: File): void {
  emit('cancel', file);
  const next = props.modelValue.filter((f) => f !== file);
  emit('update:modelValue', next);
}
</script>

<template>
  <div>
    <div
      class="border-2 border-dashed border-border-strong rounded-lg p-6 text-center cursor-pointer flex items-center justify-center min-h-24"
      :class="disabled ? 'opacity-50 cursor-not-allowed' : ''"
      role="button"
      tabindex="0"
      @click="openPicker"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
      @dragover.prevent
      @drop.prevent="onDrop"
    >
      <p class="text-base text-muted">Drop documents here or click to upload</p>
      <input
        ref="fileInput"
        type="file"
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        multiple
        class="hidden"
        :disabled="disabled"
        @change="onFileChange"
      />
    </div>
    <p class="text-sm text-muted mt-2">
      Up to {{ maxFiles }} documents, PDF or Word, 20MB each.
    </p>
    <p v-if="uploadError" class="text-sm text-danger mt-2" role="alert">
      {{ uploadError }}
    </p>
    <div
      v-if="modelValue.length > 0"
      class="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4"
    >
      <template v-for="(file, i) in modelValue" :key="`${file.name}-${i}`">
        <!-- D-37 / B1: uploading tile -->
        <div
          v-if="isUploading(file)"
          data-testid="upload-tile-uploading"
          class="relative rounded-md border border-border bg-surface p-2 flex flex-col gap-1"
        >
          <FontAwesomeIcon
            :icon="['far', 'file-pdf']"
            class="text-foreground text-2xl"
            data-testid="upload-tile-icon"
          />
          <span
            data-testid="upload-tile-filename"
            class="text-xs break-all line-clamp-2 pr-5 text-foreground"
            >{{ file.name }}</span
          >
          <span data-testid="upload-tile-size" class="text-xs text-muted">{{
            formatBytes(file.size)
          }}</span>
          <div
            data-testid="upload-tile-progress"
            role="progressbar"
            :aria-valuenow="progressOf(file)"
            aria-valuemin="0"
            aria-valuemax="100"
            class="h-1.5 bg-border rounded-full overflow-hidden"
          >
            <div
              class="h-full bg-primary transition-all"
              :style="{ width: `${progressOf(file)}%` }"
            />
          </div>
          <button
            type="button"
            data-testid="upload-tile-cancel"
            aria-label="Cancel upload"
            class="absolute top-1 right-1 text-muted hover:text-danger"
            @click="onCancel(file)"
          >
            <FontAwesomeIcon :icon="['fas', 'xmark']" />
          </button>
        </div>
        <!-- D-37 / B1: done tile -->
        <div
          v-else
          data-testid="upload-tile-done"
          class="relative rounded-md border border-border bg-surface p-2 flex flex-col gap-1"
        >
          <FontAwesomeIcon
            :icon="['far', 'file-pdf']"
            class="text-foreground text-2xl"
            data-testid="upload-tile-icon"
          />
          <span
            data-testid="upload-tile-filename"
            class="text-xs break-all line-clamp-2 pr-5 text-foreground"
            >{{ file.name }}</span
          >
          <span data-testid="upload-tile-size" class="text-xs text-muted">{{
            formatBytes(file.size)
          }}</span>
          <button
            type="button"
            data-testid="upload-tile-delete"
            aria-label="Remove file"
            class="absolute top-1 right-1 text-muted hover:text-danger"
            @click="removeFile(i)"
          >
            <FontAwesomeIcon :icon="['fas', 'xmark']" />
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
